/**
 * Generates an array of consecutive EBAB periods - each in the usual YYYY-Q# format -
 * for a given number of quarters, up to the endPeriod passed in.
 * @param {String} endPeriod - The final period to generate up to
 * @param {Number} numQuarters - The number of quarters to generate
 * @returns {Array<String>} - The array of EBAB periods
 */
export function generateEbabPeriods(endPeriod, numQuarters) {
    const [yearString, quarterString] = endPeriod.split('-');

    let currentYear = parseInt(yearString);
    let currentQuarter = parseInt(quarterString.replace('Q', ''));

    let periodArray = new Array(numQuarters);
    for (let i = numQuarters - 1; i >= 0; i--) {
        periodArray[i] = `${currentYear}-Q${currentQuarter}`;

        if (currentQuarter === 1) {
            currentQuarter = 4;
            currentYear--;
        } else {
            currentQuarter--;
        }
    }

    return periodArray;
}

/**
 * Function to calculate minimum and maximums from EBAB score data (baScore and medianBaScore)
 * to be used in the BA Scores Over Time graph Y axis. Domain calculated with a given yPadding on each side,
 * up to a min and max of 0 and 100 respectively.
 * @param {Array<Object>} overTimeData - over time EBAB data for a portfolio
 * @param {Number} yPadding - domain padding either side of the data min and max values
 * @returns {number[]} - minimum and maximum values for Y axis padding
 */
export function calculateScoresYDomain(overTimeData, yPadding) {
    const allScores = overTimeData
        .map((d) => [...[d.baScore, d.peerGroupMetrics.medianBaScore]])
        .flat();

    const minYValue = Math.max(Math.min(...allScores) - yPadding, 0);
    const maxYValue = Math.min(Math.max(...allScores) + yPadding, 100);

    return [minYValue, maxYValue];
}

/**
 * Merges an array of ebab periods with a portfolio's time series data into an array of objects
 * representing data for consecutive quarters going back as far as the earliest quarter in the periodsArray.
 * @param {Array<string>} periodsArray - array of ebab period strings
 * @param {Array<Object>} overTimeData - selected portfolio's time series data
 * @returns {Array<Object>} - the merged array
 */
export function backFillMissingPeriods(periodsArray, overTimeData) {
    return (
        periodsArray
            //filter out periods that exist in the overTimeData
            .filter(
                (period) =>
                    !overTimeData?.some((item) => item.ebabPeriod === period)
            )
            //create an object for each period with a ebabPeriod attribute
            .map((period) => ({ ebabPeriod: period }))
            //concatenate (handle empty overTimeData)
            .concat(overTimeData ?? [])
            //sort by ebabPeriod ascending
            .sort((a, b) => (a.ebabPeriod > b.ebabPeriod ? 1 : -1))
    );
}

/**
 * Helper function to convert EBAB periods from standard format (YYYY-Q#)
 * to a reversed format used in the over time chart (Q# YY) or (Q# YYYY)
 * @param {String} period - EBAB period to convert
 * @param {Boolean} displayFullYear - if true display full year string (YYYY)
 * @returns {String} - EBAB period in shorter format
 */
export function convertEbabPeriod(period, displayFullYear = false) {
    const split = period.split('-');
    return displayFullYear
        ? split[1] + ' ' + split[0]
        : split[1] + ' ' + split[0].slice(-2);
}
